import React, { useEffect, useState } from 'react';
import { useDocumentMeta } from './useDocumentMeta';
import { BOOKING_URL } from './siteConfig';
import './AboutPage.css';

/**
 * The manual-process diagram is the one moving element on the site (rule
 * 13b). Only a GIF exists in src/Images/ (no MP4/WebM yet), so a GIF can't
 * be paused the way <video poster> can. This swaps to the static poster
 * frame under prefers-reduced-motion instead, including live toggles of
 * the OS setting while the page is open.
 */
const ManualProcessDiagram: React.FC = () => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="manual-process">
      <img
        src={
          reducedMotion
            ? require('./Images/about-manual-process.png')
            : require('./Images/Manual-Pitch-Process.gif')
        }
        alt="The old manual process: the same release details typed again into a separate form for each platform"
        className="manual-process-media"
      />
    </div>
  );
};

const AboutPage: React.FC = () => {
  useDocumentMeta(
    'About Cadence IO',
    'Cadence holds your release marketing information, delivers it to the partners you choose, and tracks performance after release. Currently in private beta.'
  );

  return (
    <div className="aboutpage">
      <section className="about-hero">
        <div
          className="about-hero-bg"
          style={{ backgroundImage: `url(${require('./Images/hero-release-record.png')})` }}
        />
        <div className="about-hero-scrim" />
        <div className="about-hero-content">
          <h1>Distribution solved the audio file. It stopped there.</h1>
          <p className="about-hero-subhead">Everything else a release needs still moves by hand.</p>
        </div>
      </section>

      <section className="why-it-exists">
        <div className="why-it-exists-column">
          <h2 className="why-heading">Every platform wants the same information, in a different form.</h2>
          <p className="why-body">A decade ago a release went to one place that everyone read. Now every platform has its own intake, its own form, its own required fields, and its own idea of what a submission should contain.</p>
          <p className="why-emphasis">The surface area multiplied. <span className="teal-accent">The teams did not.</span></p>
          <p className="why-body">So the work became copying. The same information, typed again for every destination, by someone whose job was supposed to be something else. And once the release was out, nothing kept a record of what had been sent or where.</p>
        </div>
      </section>

      <section className="diagram-quote-row">
        <div className="diagram-col">
          <ManualProcessDiagram />
        </div>
        <div className="quote-col">
          <p className="pull-quote-inline">Music distribution solved this for the audio file. We just stopped building at the audio file.</p>
        </div>
      </section>

      <section className="old-way">
        <p className="eyebrow">Why Cadence</p>
        <h2 className="old-way-heading">
          Built for release information.
          <br />
          <span className="old-way-heading-muted">Not for filling in forms faster.</span>
        </h2>

        <div className="old-way-table">
          <p className="old-way-col-label old-way-col-label-old">The old way</p>
          <p className="old-way-col-label old-way-col-label-new">With Cadence</p>

          <p className="old-way-cell old-way-cell-old">The same release information, retyped into every platform's form.</p>
          <p className="old-way-cell old-way-cell-new">One entry, delivered to each platform in the format that one asks for.</p>

          <p className="old-way-cell old-way-cell-old">Artist details retyped from scratch on every release.</p>
          <p className="old-way-cell old-way-cell-new">Artists carry their IDs, links, and details from one release to the next.</p>

          <p className="old-way-cell old-way-cell-old">Partner and editor emails written by hand, when there is time.</p>
          <p className="old-way-cell old-way-cell-new">Contacts sit on the release. The notifications go from the same place.</p>

          <p className="old-way-cell old-way-cell-old">Release details in a spreadsheet that connects to nothing.</p>
          <p className="old-way-cell old-way-cell-new">The spreadsheet you already keep, connected, flowing straight in.</p>

          <p className="old-way-cell old-way-cell-old">Guessing at mood and genre, form after form.</p>
          <p className="old-way-cell old-way-cell-new">Upload the track and those categories fill themselves in.</p>

          <p className="old-way-cell old-way-cell-old">After release, no record of what was sent or where.</p>
          <p className="old-way-cell old-way-cell-new">Placements, popularity, and radio spins attached to the release they came from.</p>
        </div>
      </section>

      <section className="what-it-does-today">
        <div className="today-columns">
          <div className="today-column">
            <div className="today-image">
              <img src={require('./Images/about-spreadsheet.png')} alt="A spreadsheet connected to Cadence" />
            </div>
            <p className="today-label">Connect</p>
            <h3 className="today-heading">Your spreadsheet, connected.</h3>
            <p className="today-line">Google Sheets or Airtable flows straight in, and artists carry their IDs from one release to the next.</p>
          </div>

          <div className="today-column">
            <div className="today-image">
              <img src={require('./Images/about-pitch-form.png')} alt="A pitch form ready to submit to a platform" />
            </div>
            <p className="today-label">Deliver</p>
            <h3 className="today-heading">One entry, every destination.</h3>
            <p className="today-line">Delivered by form and by email, in the format each platform asks for.</p>
          </div>

          <div className="today-column">
            <div className="today-image">
              <img src={require('./Images/block-4-performance.png')} alt="Performance data attached to a release in Cadence" />
            </div>
            <p className="today-label">Track</p>
            <h3 className="today-heading">Performance on the release.</h3>
            <p className="today-line">Popularity, radio spins, and playlist placements sit against the release they came from.</p>
          </div>
        </div>
      </section>

      <section className="about-close">
        <p className="about-close-line">Cadence is in private beta, onboarding a small number of teams at a time.</p>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="about-close-cta"
        >
          Book a call
        </a>
      </section>

      <section className="who-behind">
        <img
          src={require('./Images/sam-headshot.jpg')}
          alt="Sam Friske"
          className="who-behind-photo"
        />
        <div className="who-behind-text">
          <p className="who-behind-name">Sam Friske, Founder</p>
          <p className="who-behind-credential">Label operations at Universal and Concord, and with the Aftermath production team.</p>
          <p className="who-behind-paragraph">So much of my career in music was spent moving release information by hand. Forms and emails, internal and external, the same details typed again for every destination. Cadence is the tool I wanted while I was doing that job.</p>
          <p className="who-behind-email">
            <a href="mailto:sam@cadenceio.com">sam@cadenceio.com</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
